import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js";
import { Leg } from "./../../types";
import { calculateProfit } from "./../../calculations/calculateProfits";
import { analyzePositions } from "./../../calculations/analyzeProfits";
import { ExpiryAnalysis } from "./expiryAnalysis";

function generateLabels(
  positions: Leg[],
  min: number,
  max: number,
  putBreakEven: number | undefined,
  callBreakEven: number | undefined
) {
  const labels = positions.map((value: Leg) => value.strikePrice).sort();
  const sortedPositions = positions.sort(
    (a, b) => a.strikePrice - b.strikePrice
  );
  labels.splice(0, 0, Math.max(labels[0] - sortedPositions[0].price, 0));
  labels.push(
    parseInt(labels[labels.length - 1].toString()) +
      sortedPositions[sortedPositions.length - 1].price
  );

  if (min !== Number.NEGATIVE_INFINITY && min < labels[0]) {
    labels.splice(0, 0, min);
  }

  if (max !== Number.POSITIVE_INFINITY && max > labels[labels.length - 1]) {
    labels.push(max);
  }

  if (putBreakEven && putBreakEven < labels[0]) {
    labels.splice(0, 0, putBreakEven);
  }

  if (callBreakEven && callBreakEven < labels[0]) {
    labels.splice(0, 0, callBreakEven);
  }

  if (callBreakEven && callBreakEven > labels[labels.length - 1]) {
    labels.push(callBreakEven);
  }

  if (putBreakEven && putBreakEven > labels[labels.length - 1]) {
    labels.push(putBreakEven);
  }

  if (labels.length < 4) {
    labels.splice(0, 0, Math.max(labels[0] - sortedPositions[0].price, 0));
    labels.push(
      parseInt(labels[labels.length - 1].toString()) +
        sortedPositions[sortedPositions.length - 1].price
    );
  }
  return labels;
}

export function PayOffGraph(props: any) {
  const { width, height, strategy } = props;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const [strike, setStrike] = useState({
    min: Number.NEGATIVE_INFINITY,
    max: Number.POSITIVE_INFINITY,
  });
  const analysis = analyzePositions(strategy);
  const { maxProfit, maxLoss, breakEven } = analysis;
  const labels = generateLabels(
    strategy,
    strike.min,
    strike.max,
    breakEven.putBreakEven,
    breakEven.callBreakEven
  );
  const handleMinimumStrikeChange = (event: any) => {
    setStrike({ ...strike, min: parseFloat(event.target.value) });
  };

  const handleMaximumStrikeChange = (event: any) => {
    setStrike({ ...strike, max: parseFloat(event.target.value) });
  };
  const dataset = labels.map((value: number) => {
    return { x: value, y: 0 };
  });
  const chartOptions = {
    type: "line",
    tooltips: {
      mode: "nearest",
      intersect: false,
    },
    interaction: {
      mode: "nearest",
      intersect: false,
    },

    data: {
      //labels: labels,
      datasets: [
        {
          label: "Payoff", // Name the series
          data: dataset, // Specify the data values array
          fill: true,
          borderColor: "rgb(26, 194, 54)", // Add custom color border (Line)
          backgroundColor: "rgba(26, 194, 54, 0.3)", // Add custom color background (Points and Fill)
          borderWidth: 1, // Specify bar border width
          lineTension: 0,
          pointHitRadius: 10,
        },
      ],
    },
    options: {
      scales: {
        xAxes: [
          {
            type: "linear",
            position: "bottom",
          },
        ],
        yAxes: [
          {
            ticks: {
              callback: function (label: any) {
                return Math.abs(label) > 1000 ? label / 1000 + " k" : label;
              },
            },
            scaleLabel: {
              display: true,
              labelString: "1k = 1000",
            },
          },
        ],
      },
      responsive: true, // Instruct chart js to respond nicely.
      maintainAspectRatio: false, // Add to prevent default behaviour of full-width/height
    },
  };
  useEffect(() => {
    if (chartRef && chartRef.current) {
      if ((window as any).chart !== undefined) {
        (window as any).chart.destroy();
      }
      const ctx = chartRef.current;
      (window as any).chart = new Chart(ctx, chartOptions);
      setTimeout(() => {
        const newLabels = (window as any).chart.scales["x-axis-0"]
          .ticksAsNumbers;
        const newData = newLabels.map((value: number) => {
          return {
            x: value,
            y: calculateProfit(strategy, value),
          };
        });
        (window as any).chart.data.datasets[0].data = newData;
        (window as any).chart.update();
      }, 200);
    }
  }, [width, height, strategy.length, strike.min, strike.max]);
  return (
    <div
      style={{ marginTop: "-10px", marginBottom: "10px" }}
      className="e-field"
    >
      <div>
        <div className="box">
          <div className="max-profit">Max profit: {maxProfit}</div>
          <div className="max-loss">Max loss: {maxLoss}</div>
          {breakEven.callBreakEven && (
            <div className="call-break-even">
              Call Breakeven: {breakEven.callBreakEven}
            </div>
          )}
          {breakEven.putBreakEven && (
            <div className="put-break-even">
              Put Breakeven: {breakEven.putBreakEven}
            </div>
          )}
        </div>
        <ExpiryAnalysis {...props.expiryAnalysisProps}></ExpiryAnalysis>
      </div>
      <p>
        <b>Payoff Graph</b>
      </p>
      <input
        type="number"
        value={strike.min}
        placeholder="Min strike"
        onChange={handleMinimumStrikeChange}
        style={{ width: "40%", margin: "5px" }}
      />

      <input
        type="number"
        value={strike.max}
        placeholder="Max strike"
        onChange={handleMaximumStrikeChange}
        style={{ width: "40%", margin: "5px" }}
      />

      <canvas
        ref={chartRef}
        style={{ minHeight: "300px", maxHeight: "400px" }}
      ></canvas>
    </div>
  );
}
